import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useSearchParams } from "react-router-dom";
import { apiGet, apiPatch, apiPost } from "../../lib/api";

type MiniAppBootstrap = {
  now: string;
  tenant_id: string;
  tenant_name: string;
  user_id: string;
  role: string;
  branch_id?: string | null;
  today_status_counts: {
    pending_confirmation: number;
    confirmed: number;
    completed: number;
    cancelled: number;
    no_show: number;
  };
  waitlist_open_today: number;
  upcoming_appointments: Array<{
    id: string;
    customer_id: string;
    customer_name?: string | null;
    service_name?: string | null;
    staff_name?: string | null;
    branch_name?: string | null;
    start_at: string;
    status: string;
    allowed_actions: string[];
  }>;
  focus: {
    route?: string;
    appointment_id?: string;
    waitlist_id?: string;
    customer_id?: string;
  };
};

type WaitlistStatus = "open" | "contacted" | "booked" | "closed";
type WaitlistItem = {
  id: string;
  customer_id: string;
  customer_name?: string | null;
  customer_phone?: string | null;
  service_name?: string | null;
  preferred_staff_name?: string | null;
  notes?: string | null;
  status: WaitlistStatus;
};

function getTelegramWebApp() {
  return (window as unknown as { Telegram?: { WebApp?: { ready?: () => void; expand?: () => void } } }).Telegram?.WebApp;
}

export function TelegramManagerQuickPage() {
  const [searchParams] = useSearchParams();
  const qc = useQueryClient();
  const [waitlistNotice, setWaitlistNotice] = useState("");

  useEffect(() => {
    try {
      getTelegramWebApp()?.ready?.();
      getTelegramWebApp()?.expand?.();
    } catch {
      // no-op outside Telegram
    }
  }, []);

  const qs = useMemo(() => {
    const params = new URLSearchParams();
    for (const key of ["branch_id", "appointment_id", "waitlist_id", "customer_id"]) {
      const value = searchParams.get(key);
      if (value) params.set(key, value);
    }
    const str = params.toString();
    return str ? `?${str}` : "";
  }, [searchParams]);

  const queryKey = useMemo(() => ["telegram-miniapp-bootstrap", qs], [qs]);
  const bootstrap = useQuery({
    queryKey,
    queryFn: () => apiGet<MiniAppBootstrap>(`/v3/telegram-miniapp/bootstrap${qs}`),
    refetchInterval: 30000,
  });

  const transition = useMutation({
    mutationFn: ({ id, action }: { id: string; action: string }) =>
      apiPost(`/v3/calendar/appointments/${id}/transition`, { action }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey });
    },
  });

  const waitlistDay = useMemo(() => {
    const now = bootstrap.data?.now;
    if (!now) return "";
    return new Date(now).toISOString().slice(0, 10);
  }, [bootstrap.data?.now]);

  const waitlistBranchId = bootstrap.data?.branch_id || searchParams.get("branch_id") || "";
  const waitlistKey = useMemo(
    () => ["telegram-miniapp-waitlist", waitlistBranchId, waitlistDay],
    [waitlistBranchId, waitlistDay]
  );
  const waitlist = useQuery({
    queryKey: waitlistKey,
    enabled: Boolean(waitlistDay),
    queryFn: () =>
      apiGet<{ items: WaitlistItem[] }>(
        `/v3/calendar/waitlist?day=${encodeURIComponent(waitlistDay)}${waitlistBranchId ? `&branch_id=${encodeURIComponent(waitlistBranchId)}` : ""}`
      ),
    refetchInterval: 30000,
  });

  const updateWaitlist = useMutation({
    mutationFn: ({ id, status }: { id: string; status: WaitlistStatus }) => apiPatch(`/v3/calendar/waitlist/${id}`, { status }),
    onSuccess: async () => {
      setWaitlistNotice("Waitlist updated.");
      await qc.invalidateQueries({ queryKey: waitlistKey });
      await qc.invalidateQueries({ queryKey });
    },
    onError: (err) => setWaitlistNotice(String(err)),
  });

  const bookingLink = useMutation({
    mutationFn: (id: string) => apiPost<{ url: string }>(`/v3/calendar/waitlist/${id}/booking-link`, {}),
    onSuccess: async (data) => {
      const absolute = `${window.location.origin}${data.url}`;
      try {
        await navigator.clipboard.writeText(absolute);
        setWaitlistNotice("Booking link copied.");
      } catch {
        setWaitlistNotice(absolute);
      }
      await qc.invalidateQueries({ queryKey: waitlistKey });
    },
    onError: (err) => setWaitlistNotice(String(err)),
  });

  const focusRoute = bootstrap.data?.focus?.route || "/app/calendar";
  const quickActions = new Set(["confirm", "cancel", "complete", "no_show"]);
  const actionableWaitlist = (waitlist.data?.items ?? []).filter((item) => item.status === "open" || item.status === "contacted");

  return (
    <div className="min-h-screen bg-slate-950 text-white p-3 sm:p-4">
      <div className="mx-auto max-w-3xl grid gap-3">
        <section className="rounded-2xl border border-white/10 bg-white/5 p-4">
          <p className="text-[11px] uppercase tracking-[0.16em] text-slate-300">Telegram Mini App</p>
          <h1 className="text-xl font-semibold mt-2">{bootstrap.data?.tenant_name || "Salon OS"}</h1>
          <p className="text-sm text-slate-300 mt-1">Quick actions for today, waitlist, and focused items.</p>
          <div className="mt-3 flex flex-wrap gap-2">
            <Link className="btn" to={focusRoute}>
              Open Focus
            </Link>
            <Link className="btn secondary" to="/app/calendar">
              Full Calendar
            </Link>
            <Link className="btn secondary" to="/app/clients">
              Clients
            </Link>
          </div>
        </section>

        <section className="rounded-2xl border border-white/10 bg-white/5 p-4">
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-sm">
            <div className="rounded-xl bg-white/5 border border-white/10 p-3">
              <div className="text-slate-300 text-xs">Pending</div>
              <div className="text-lg font-semibold">{bootstrap.data?.today_status_counts.pending_confirmation ?? "-"}</div>
            </div>
            <div className="rounded-xl bg-white/5 border border-white/10 p-3">
              <div className="text-slate-300 text-xs">Confirmed</div>
              <div className="text-lg font-semibold">{bootstrap.data?.today_status_counts.confirmed ?? "-"}</div>
            </div>
            <div className="rounded-xl bg-white/5 border border-white/10 p-3">
              <div className="text-slate-300 text-xs">Waitlist Open</div>
              <div className="text-lg font-semibold">{bootstrap.data?.waitlist_open_today ?? "-"}</div>
            </div>
          </div>
          {bootstrap.isLoading ? <p className="text-sm text-slate-300 mt-3">Loading Mini App data...</p> : null}
          {bootstrap.error ? <p className="text-sm text-rose-300 mt-3">{String(bootstrap.error)}</p> : null}
        </section>

        <section className="rounded-2xl border border-white/10 bg-white/5 p-4">
          <div className="flex items-center justify-between gap-2">
            <h2 className="text-base font-semibold">Upcoming Appointments</h2>
            <span className="text-xs text-slate-300">{bootstrap.data?.upcoming_appointments.length ?? 0} items</span>
          </div>

          <div className="grid gap-2 mt-3">
            {(bootstrap.data?.upcoming_appointments ?? []).map((item) => (
              <article key={item.id} className="rounded-xl border border-white/10 bg-white/5 p-3">
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div>
                    <div className="font-semibold">{item.customer_name || "Client"}</div>
                    <div className="text-sm text-slate-300 mt-1">
                      {(item.service_name || "Service")} • {(item.staff_name || "Staff")}
                    </div>
                    <div className="text-xs text-slate-400 mt-1">
                      {new Date(item.start_at).toLocaleString()} • {item.status}
                    </div>
                  </div>
                  <Link className="btn secondary" to={`/app/calendar?appointment_id=${encodeURIComponent(item.id)}`}>
                    Open
                  </Link>
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  {item.allowed_actions.filter((action) => quickActions.has(action)).map((action) => (
                    <button
                      key={`${item.id}:${action}`}
                      className="btn secondary"
                      onClick={() => transition.mutate({ id: item.id, action })}
                      disabled={transition.isPending}
                    >
                      {action}
                    </button>
                  ))}
                </div>
              </article>
            ))}
            {!bootstrap.isLoading && !(bootstrap.data?.upcoming_appointments.length ?? 0) ? (
              <p className="text-sm text-slate-300">No upcoming appointments.</p>
            ) : null}
          </div>
        </section>

        <section className="rounded-2xl border border-white/10 bg-white/5 p-4">
          <div className="flex items-center justify-between gap-2">
            <h2 className="text-base font-semibold">Waitlist</h2>
            <span className="text-xs text-slate-300">{actionableWaitlist.length} actionable</span>
          </div>
          {waitlistNotice ? <p className="text-xs text-emerald-300 mt-2">{waitlistNotice}</p> : null}
          {waitlist.isLoading ? <p className="text-sm text-slate-300 mt-3">Loading waitlist...</p> : null}
          {waitlist.error ? <p className="text-sm text-rose-300 mt-3">{String(waitlist.error)}</p> : null}
          <div className="grid gap-2 mt-3">
            {actionableWaitlist.slice(0, 8).map((item) => (
              <article key={item.id} className="rounded-xl border border-white/10 bg-white/5 p-3">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <div className="font-semibold">{item.customer_name || "Client"}</div>
                    <div className="text-sm text-slate-300 mt-1">
                      {item.service_name || "Service"}
                      {item.preferred_staff_name ? ` • ${item.preferred_staff_name}` : ""}
                    </div>
                    {item.customer_phone ? <div className="text-xs text-slate-400 mt-1">{item.customer_phone}</div> : null}
                    {item.notes ? <div className="text-xs text-slate-400 mt-1">{item.notes}</div> : null}
                  </div>
                  <span className="rounded-lg border border-white/10 bg-white/5 px-2 py-1 text-[11px] uppercase tracking-wide text-slate-200">
                    {item.status}
                  </span>
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  <button className="btn secondary" onClick={() => bookingLink.mutate(item.id)} disabled={bookingLink.isPending}>
                    Copy Link
                  </button>
                  <button
                    className="btn secondary"
                    onClick={() => updateWaitlist.mutate({ id: item.id, status: "contacted" })}
                    disabled={updateWaitlist.isPending || item.status === "contacted"}
                  >
                    Contacted
                  </button>
                  <button
                    className="btn secondary"
                    onClick={() => updateWaitlist.mutate({ id: item.id, status: "closed" })}
                    disabled={updateWaitlist.isPending}
                  >
                    Close
                  </button>
                </div>
              </article>
            ))}
            {!waitlist.isLoading && !actionableWaitlist.length ? (
              <p className="text-sm text-slate-300">No actionable waitlist entries today.</p>
            ) : null}
          </div>
        </section>
      </div>
    </div>
  );
}
